import {RemoteConstantsModel} from "../../models/remoteConstants.model";
import {MapSectionKey, RecordSectionKey, SectionDiff, isRecordSection} from "../../utils/branchDiff";
import {
    classifyRecordMigration,
    classifyRecordRemoval,
    classifyRecordUpdate,
    findRecordByIdentity,
} from "../../utils/migrateRecord";
import {classifyGlobalResolve, collectCurrentKnownIds} from "../../utils/resolveDifference";
import {GlobalResolveAction} from "../../utils/resolveDifference";
import {CommandBase} from "../../commands/commandBase";
import {MigrateRecordCommand} from "../../commands/migrateRecord.command";
import {UpdateRecordCommand} from "../../commands/updateRecord.command";
import {RemoveRecordCommand} from "../../commands/removeRecord.command";
import {SetMapEntryCommand} from "../../commands/setMapEntry.command";
import {RemoveMapEntryCommand} from "../../commands/removeMapEntry.command";
import {ResolveGlobalFieldCommand} from "../../commands/resolveGlobalField.command";

// Per-section action model shared by the summary bar (counts) and the "Take all
// resolvable" bulk button. `ready` is defined as exactly what the bulk action
// would apply, so the headline number and the button never disagree.
export interface SectionActionSummary {
    total: number;
    ready: number;
    blocked: number;
    bulkCommands: CommandBase[];
}

type NameResolver = (key: string) => string;

// ---- Map access helpers (shared with mapSectionView) ----------------------

export function mapSectionRoot(model: RemoteConstantsModel, sectionKey: MapSectionKey): unknown {
    switch (sectionKey) {
        case 'localization': return model.localization;
        case 'sounds':       return model.sounds;
        case 'images':       return model.images;
        case 'imagePacks':   return model.imagePacks;
        case 'constructionImagePacks': return model.constructionImagePacks;
    }
}

export function readPath(root: unknown, path: string[]): unknown {
    let cur: unknown = root;
    for (const seg of path) {
        if (cur === null || typeof cur !== 'object') return undefined;
        cur = (cur as Record<string, unknown>)[seg];
    }
    return cur;
}

// Image-metadata rows an image pack references but Current lacks — bundled with
// a pack migration so it doesn't land pointing at missing images. Handles both
// imagePacks (string[][] — level → variations) and constructionImagePacks
// (string[] — ordered frame keys).
export function extraImagesForPack(
    sectionKey: MapSectionKey,
    value: unknown,
    targetData: RemoteConstantsModel,
    current: RemoteConstantsModel,
): { key: string; value: unknown }[] {
    if (!Array.isArray(value)) return [];
    const referenced = new Set<string>();
    if (sectionKey === 'imagePacks') {
        for (const variations of value as unknown[])
            if (Array.isArray(variations)) for (const k of variations) if (typeof k === 'string') referenced.add(k);
    } else if (sectionKey === 'constructionImagePacks') {
        for (const k of value as unknown[]) if (typeof k === 'string') referenced.add(k);
    } else {
        return [];
    }
    const out: { key: string; value: unknown }[] = [];
    referenced.forEach(k => {
        const targImg = (targetData.images as Record<string, unknown> | undefined)?.[k];
        if (targImg === undefined) return;
        if ((current.images as Record<string, unknown> | undefined)?.[k] !== undefined) return;
        out.push({key: k, value: targImg});
    });
    return out;
}

function setMapCommandFor(
    sectionKey: MapSectionKey,
    path: string[],
    targetData: RemoteConstantsModel,
    current: RemoteConstantsModel,
): SetMapEntryCommand {
    const value = readPath(mapSectionRoot(targetData, sectionKey), path);
    return new SetMapEntryCommand(sectionKey, path, value, extraImagesForPack(sectionKey, value, targetData, current));
}

// ---------------------------------------------------------------------------

export function buildSectionActions(
    section: SectionDiff,
    current: RemoteConstantsModel,
    targetData: RemoteConstantsModel,
    resolveName: NameResolver,
    currentBranchLabel: string,
): SectionActionSummary {
    const bulkCommands: CommandBase[] = [];
    let total = 0;
    let ready = 0;

    if (section.flavor === 'record' && isRecordSection(section.sectionKey)) {
        const sectionKey = section.sectionKey as RecordSectionKey;

        for (const e of section.onlyInTarget) {
            total++;
            const rec = findRecordByIdentity(targetData, sectionKey, e.identity);
            if (!rec) continue;
            const cls = classifyRecordMigration(rec, sectionKey, targetData, current, resolveName, currentBranchLabel);
            if (cls.resolvable && cls.migration) { ready++; bulkCommands.push(new MigrateRecordCommand(cls.migration)); }
        }
        for (const e of section.onlyInCurrent) {
            total++;
            const rec = findRecordByIdentity(current, sectionKey, e.identity);
            if (!rec) continue;
            const cls = classifyRecordRemoval(rec, sectionKey, current, resolveName);
            if (cls.removable) { ready++; bulkCommands.push(new RemoveRecordCommand(sectionKey, e.identity)); }
        }
        for (const d of section.differs) {
            total++;
            const rec = findRecordByIdentity(targetData, sectionKey, d.identity);
            if (!rec) continue;
            const cls = classifyRecordUpdate(rec, sectionKey, targetData, current, resolveName);
            if (cls.resolvable && cls.migration) { ready++; bulkCommands.push(new UpdateRecordCommand(cls.migration)); }
        }
    } else if (section.flavor === 'map') {
        const sectionKey = section.sectionKey as MapSectionKey;
        for (const e of section.onlyInTarget) {
            total++; ready++;
            bulkCommands.push(setMapCommandFor(sectionKey, e.path ?? [e.identity], targetData, current));
        }
        for (const e of section.onlyInCurrent) {
            total++; ready++;
            bulkCommands.push(new RemoveMapEntryCommand(sectionKey, e.path ?? [e.identity]));
        }
        for (const d of section.differs) {
            total++; ready++;
            bulkCommands.push(setMapCommandFor(sectionKey, d.path ?? [d.identity], targetData, current));
        }
    } else if (section.flavor === 'globals') {
        const fieldChanges = section.differs[0]?.fieldChanges ?? [];
        const knownIds = collectCurrentKnownIds(current);
        const actions: GlobalResolveAction[] = [];
        for (const fc of fieldChanges) {
            total++;
            const cls = classifyGlobalResolve(fc, knownIds, knownIds);
            if (cls.resolvable) { ready++; actions.push(...cls.actions); }
        }
        if (actions.length > 0) bulkCommands.push(new ResolveGlobalFieldCommand(actions));
    }

    return {total, ready, blocked: total - ready, bulkCommands};
}
